import { Injectable } from '@angular/core';

import * as dicomParser from 'dicom-parser'; // npm install dicom-parser
import * as cornerstone from 'cornerstone-core'; // npm install cornerstone-core
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';  // npm install cornerstone-wado-image-loader

import * as dcmjs from 'dcmjs';  // npm i dcmjs

const TRANSFER_SYNTAX_UID = '1.2.840.10008.1.2.1';

// Cornerstone initialization
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;


// Services
import { UserAuthenticationService } from '../firestore/user-authentication.service';
import { HttpService } from '../http/http.service';
import { FirestoreService } from '../firestore/firestore.service';
import { UtilsService } from '../utils/utils.service';

// Constants
import { GlobalVariables } from '../../constants/global';

// Cloud function strings (environment dependent)
import { environment } from '../../../environments/environment';



@Injectable({
  providedIn: 'root'
})
export class DicomService {

  // ----- Class variables ----- //
  // HU visualization range
  visualization_minHU = -150
  visualization_maxHU = 200
  // Files to avoid
  filenamesToAvoid = ['DICOMDIR', '.DS_Store', '._.DS_Store']
  // Variables to be managed by the UI
  currentProcessIndex:any = null
  dicomdir_structure:any = null
  currentPatientList:any  = null
  currentStudyList:any = null
  currentSerieList:any = null
  currentPatientId = ''
  currentStudyId = ''
  currentSerieId = ''
  currentFirebaseHash = ''
  instanceUIDsDict:any = null
  // Variables to load serie data
  currentUploadingInformationDict = {}
  // Current slice variables to be used in the modify popup
  currentSlice:number = null
  currentPixelData:any = null
  currentRescaleSlope:number = null
  currentRescaleItercept:number = null
  currentImageOrientation:number[] = null

  // Min slice spacing for positioning image
  minSliceSpacing:number = GlobalVariables.dcmImagesUploading['minSliceSpacing']
  largestJump:number = GlobalVariables.dcmImagesUploading['largestJump']

  // Variables for dcm inspection when patient_id, study_uid and series_uid has been delete
  allowBadlyAnonimizedDcm:boolean = GlobalVariables.dcmImagesUploading['allowBadlyAnonimizedDcm']
  badlyAnonymizedId:string = GlobalVariables.dcmImagesUploading['badlyAnonymizedId']
  thereIsBadlyAnonymizeData:boolean = false // To notify the user with a popup after loading the data
  
  // Variables for being used in the http request
  sliceSpacing:number = null


  dicomKeysToKeep = [
    '_vrMap',
    'SOPClassUID', 'SOPInstanceUID', 'TransferSyntaxUID',
    'ImageType', 'Modality',
    'StudyDate',
    'SliceThickness', 'PixelSpacing', 'SeriesNumber', 
    'ImagePositionPatient', 'ImageOrientationPatient', 'PatientPosition',
    'Rows', 'Columns', 'BitsAllocated', 'BitsStored', 'RescaleIntercept', 'RescaleSlope', 
    'HighBit', 'PixelRepresentation', 'SmallestImagePixelValue', 'LargestImagePixelValue',
    'PhotometricInterpretation',
    'PatientSex', 'PatientAge', 'PatientWeight', 'PatientSize',
    'PixelData',
    'SamplesPerPixel', 'RescaleType',
    // Adquisition parameters for software validation
    //  Adquisition parameters for software validation
    'SoftwareVersions', 'ConvolutionKernel', 'KVP', 'XRayTubeCurrent', 'ExposureTime',
    // Manufacturer parameters for software validation     
    'Manufacturer', 'ManufacturerModelName', 
    // Contrast parameters
    'ContrastBolusRoute', 'ContrastBolusAgent',
  ]




  dicomKeysToModify = [
    'PatientID',
    'StudyID',
    // Unique ids for being able to manage the data
    'StudyInstanceUID', 'SeriesInstanceUID', 'SOPInstanceUID',
  ]

  // 'PatientIdentityRemoved' = YES
  // 'DeidentificationMethod' = 'Deidentified,Descriptors removed,Patient Characteristics retained... '
  // 'StudyInstanceUID'
  // 'SeriesInstanceUID'
  // 'SliceLocation'
  

  constructor(
    public userAuthenticationService:UserAuthenticationService,
    public httpService:HttpService,
    public firestoreService:FirestoreService,
    public utilsService:UtilsService
  ) { }




  async initializeAllVariables(){
    // Variables to be managed by the UI
    this.currentProcessIndex = null
    this.dicomdir_structure = {'PAT': {}}
    this.instanceUIDsDict = {'study':{}, 'series':{}}
    this.currentPatientList = null
    this.currentStudyList = null
    this.currentSerieList = null
    this.currentPatientId = ''
    this.currentStudyId = ''
    this.currentSerieId = ''
    // Delete the data from the backend
    if(this.currentFirebaseHash !== ''){
      // Execute the http request
      let authorizationToken = await this.userAuthenticationService.getUserIdToken()
      let userEmail = this.userAuthenticationService.getUserEmail().toString()
      let urlString = environment.googleCloudFunctions.deleteTargetDcm 
      let formData = new FormData();
      formData.append('userEmail', userEmail)
      formData.append('directoryName', this.currentFirebaseHash)
      this.httpService.executeHttpPostMethodSigned(urlString, formData, authorizationToken)
    }
    this.currentFirebaseHash = ''
    // Variables to load serie data
    this.currentUploadingInformationDict = {}
    // Current slice variables to be used in the modify popup
    this.currentSlice = null
    this.currentPixelData = null
    this.currentRescaleSlope = null
    this.currentRescaleItercept = null
    this.currentImageOrientation = null
    // Badly anonymized dicom data
    this.thereIsBadlyAnonymizeData = false
  }

  // Global parameter 'allowBadlyAnonimizedDcm' indicates if the dicom images can be loaded without valid
  // patient_id, study_uid and series_uid. If so, a custom one will be generated
  async loadDicomdirStructure(fileArray:any,  targetCallbackMethod: (data: any) => void ){
    // Clear all previous data
    await this.initializeAllVariables()
    // Set the corresponding reference in the data base so the information can be delete later on
    this.currentFirebaseHash = this.firestoreService.getFirebaseHash()
    // Load all the files under the specified directory
    let nFiles = fileArray.length
    for(let fileIndex = 0; fileIndex < nFiles; fileIndex++){
      // Read the file content into a dicom dataset. Async but wait for it to finish
      if(this.filenamesToAvoid.includes(fileArray[fileIndex].name)) continue
      let dataSet = await this.readFileAsync(fileArray[fileIndex])  
      if(dataSet === undefined) continue
      // Get patient information
      let patient_id = dataSet.string('x00100020') // Patient id
      if (patient_id === undefined){
        if (this.allowBadlyAnonimizedDcm == true) {
          patient_id = this.badlyAnonymizedId
          this.thereIsBadlyAnonymizeData = true
        } else continue
      }     
      //let boolVar = patient_id in this.dicomdir_structure['PAT']
      if (!(patient_id in this.dicomdir_structure['PAT'])){
        this.addNewPatient(dataSet, patient_id)
      }
      // Get study information
      let study_uid = dataSet.string('x0020000d') // Study uid
      if (study_uid === undefined){
        if (this.allowBadlyAnonimizedDcm == true){
          study_uid = this.badlyAnonymizedId
          this.thereIsBadlyAnonymizeData = true
        } else continue    
      } 
      if (!(study_uid in this.dicomdir_structure['PAT'][patient_id]['STU'])){
        this.addNewStudy(dataSet, patient_id, study_uid)
      }
      // Get the serie information
      let serie_uid = dataSet.string('x0020000e') // Serie uid
      if (serie_uid === undefined){
        if (this.allowBadlyAnonimizedDcm == true){
          serie_uid = this.badlyAnonymizedId
          this.thereIsBadlyAnonymizeData = true
        } else continue
      } 
      if (!(serie_uid in this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'])){
        let resultOk = this.addNewSeries(dataSet, patient_id, study_uid, serie_uid)
        if(!resultOk) continue
      }
      // Get the image data
      this.addNewImage(dataSet, patient_id, study_uid, serie_uid, fileArray[fileIndex])
    }
    // Format dicom structure and get the result dict according to how it finishes
    let resultDict = this.formatDicomdirStructure()
    // Execute the callback method
    targetCallbackMethod(resultDict)
  }






  // Read file async but wait for the result
  readFileAsync(file): Promise<dicomParser.DataSet | undefined>  {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        try{
          let arrayBuffer = reader.result as ArrayBuffer;
          let byteArray = new Uint8Array(arrayBuffer);
          let dataSet:dicomParser.DataSet = dicomParser.parseDicom(byteArray);
          resolve(dataSet);
        }catch{
          resolve(undefined)
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    })
  }


  addNewPatient(dataSet, patient_id){
    // Create the dictionary
    this.dicomdir_structure['PAT'][patient_id] = {'data': {}, 'STU': {}}
    // Add the custom data
    let informationStr = patient_id
    this.dicomdir_structure['PAT'][patient_id]['data']['informationStr'] = informationStr
  }

  addNewStudy(dataSet, patient_id, study_uid){
    // Create the dictionary
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid] = {'data': {}, 'SER': {}}
    // Add the custom data
    let study_description = dataSet.string('x00081030') // Study description
    let study_date = dataSet.string('x00080020') // Stady date
    // Generate the information string
    let informationStr = 'No study information'
    if (study_description != undefined && study_description.replaceAll(' ', '').length > 0 && study_date != undefined && study_date.replaceAll(' ', '').length > 0){
      informationStr = study_description + ' - ' + study_date
    }else if(study_description != undefined && study_description.replaceAll(' ', '').length > 0){
      informationStr = study_description
    }else if(study_date != undefined && study_date.replaceAll(' ', '').length > 0){
      informationStr = study_date
    }
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['data']['informationStr'] = informationStr
    // Generate the uid to be used when uploading the file to storage
    this.generateDicomUID('study', study_uid)
  }

  addNewSeries(dataSet, patient_id, study_uid, serie_uid){
    // Verify that the target serie corresponds to a serie modality usefull for the software
    let serie_modality = dataSet.string('x00080060') // Series modality
    if (serie_modality.toUpperCase().replaceAll(' ', '') != 'CT'){
      return false
    }  
    // Create the serie object
    // this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid] = {'data': {},'IMG': {'file': [],'zpos': []}}
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid] = {'data': {},'IMG': {'file': [],'zpos': [], 'uploaded': []}}
    // Add the custom data
    let serie_number = dataSet.string('x00200011') // Series number 
    if(serie_number){
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['seriesNumber'] = serie_number
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['informationStr'] = serie_number + ' - Images: '
    }else{
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['informationStr'] = 'Images: '
    }
    // Generate the uid to be used when uploading the file to storage
    this.generateDicomUID('series', serie_uid)
    // Return true if series was correctly added
    return true
  }

  addNewImage(dataSet, patient_id, study_uid, serie_uid, file){
    // Verify that the target image corresponds to a image type usefull for the software
    let image_type = dataSet.string('x00080008') 
    if(image_type != undefined && !image_type.split('\\').includes('AXIAL')) return
    // Get all the required parameters needed for including the target image
    //   Image position (z)
    let imagePositionZ = dataSet.floatString('x00200032', 2)
    if (imagePositionZ === undefined) return
    //   Image Rescale intercept and slope
    let image_rescaleIntercept = dataSet.floatString('x00281052')
    let image_rescaleSlope = dataSet.floatString('x00281053')
    if (image_rescaleIntercept === undefined || image_rescaleSlope === undefined) return
    //   Pixel spacing
    let image_pixelSpacing = [
      dataSet.floatString('x00280030', 0),
      dataSet.floatString('x00280030', 1),
    ];
    if (image_pixelSpacing[0] === undefined || image_pixelSpacing[1] === undefined) return
    // Store pixel spacing
    if (!('pixelSpacing' in this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid])){
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['pixelSpacing'] = image_pixelSpacing[0]
    }
    // Add the image to the corresponding lists
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['file'].push(file)
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos'].push(imagePositionZ)
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['uploaded'].push(false) // TODO -----------------------------------
    
  }


  formatDicomdirStructure(){
    let resultDict = {'resultOk': false, 'thereIsBadlyAnonymizeData':this.thereIsBadlyAnonymizeData}
    // Sort / Delete data
    for(let patient_id of Object.keys(this.dicomdir_structure['PAT'])){
      for(let study_uid of Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU'])){
        for(let serie_uid of Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'])){
          let nImages = this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos'].length
          if(nImages == 0){
            // Delete empty series
            delete this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]
          }else{
            // Add the information string
            this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['informationStr'] += nImages.toString()
            this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['sorted'] = false
          }
        }
        // Delete empty studies
        let nSeries = Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER']).length
        if(nSeries == 0){
          delete this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]
        }
      }
      // Delete empty patients
      let nStudies = Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU']).length
      if(nStudies == 0){
        delete this.dicomdir_structure['PAT'][patient_id]
      }
    }
    let nPatients = Object.keys(this.dicomdir_structure['PAT']).length
    if (nPatients > 0) resultDict['resultOk'] = true
    return resultDict
  }


  formatDicomStructure_RemoveBadlyAnonymizedData(){
    this.thereIsBadlyAnonymizeData = false
    let resultDict = {'resultOk': false, 'thereIsBadlyAnonymizeData':this.thereIsBadlyAnonymizeData}
    // Delete badly anonymized data 
    for(let patient_id of Object.keys(this.dicomdir_structure['PAT'])){
      if(patient_id == this.badlyAnonymizedId){
        delete this.dicomdir_structure['PAT'][patient_id]
      }else{
        for(let study_uid of Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU'])){
          if(study_uid == this.badlyAnonymizedId){
            delete this.dicomdir_structure['PAT'][patient_id]
          }else{
            for(let serie_uid of Object.keys(this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'])){
              if(serie_uid == this.badlyAnonymizedId){
                delete this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]
              }
            }
            // Delete empty studies
            let nSeries = this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'].length
            if(nSeries == 0){
              delete this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]
            }
          }
        }
        // Delete empty patients
        let nStudies = this.dicomdir_structure['PAT'][patient_id]['STU'].length
        if(nStudies == 0){
          delete this.dicomdir_structure['PAT'][patient_id]
        }
      }
    }
    let nPatients = Object.keys(this.dicomdir_structure['PAT']).length
    if (nPatients > 0) resultDict['resultOk'] = true
    return resultDict
  }





  // Sort serie data. Check this (reverse)
  async sortSerieData(patient_id, study_uid, serie_uid){
    let fileList = this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['file']
    let zposList = this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos']
    // Sort file list according to their z position (in reverse order)
    let fileListSorted = zposList
      .map((value, index) => ({value, file: fileList[index]}))  // Map zpos values and file in the same object 
      .sort((a, b) => a.value - b.value)  // Sort both base on zpos values
      .map(item => item.file);  // get file already sorted
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['file'] = fileListSorted.reverse()
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos'].sort((a, b) => a - b).reverse()
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['data']['sorted'] = true
    // Get serie aspect ratio
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['zPos_first'] = 
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos'].at(0)
    this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['zPos_last'] = 
      this.dicomdir_structure['PAT'][patient_id]['STU'][study_uid]['SER'][serie_uid]['IMG']['zpos'].at(-1)
  }
        


  // -------- Methods to replace id ------- //

  getUniqueIdentifier(level, dicomUid){
    if(level == 'study' || level == 'series'){
      if(dicomUid in this.instanceUIDsDict[level]) return this.instanceUIDsDict[level][dicomUid]
    }
    return this.generateDicomUID(level, dicomUid)
  }

  generateDicomUID(level, dicomUid) {
    // Get a firebase hash
    let firebaseHash = this.firestoreService.getFirebaseHash()
    // Supongamos que '2.25' es el prefijo elegido
    const prefix = '2.25';
    // Convertir hash alfanumérico a una representación numérica grande
    const numericPart = parseInt(firebaseHash.split('').map(char => char.charCodeAt(0).toString()).join('')).toString().slice(0, 38);
    // Generate the unique id
    const uid = `${prefix}.${numericPart}`
    // Update the reference dictionariy 
    if (level == 'study' || level == 'series') this.instanceUIDsDict[level][dicomUid] = uid
    // Return the uid
    return uid;
  }




  // ----------------------- Methods to interact with the main window view ----------------------- //

  // Patient information
  getPatientInformationList(){
    let nPatients = this.getNumberOfPatients()
    let patientInformationList:any = []
    for(let index=0; index<nPatients; index++){
      patientInformationList.push(this.getPatientInformationFromIndex(index))
    }
    return patientInformationList
  }
  getPatientInformationFromIndex(index){
    if (this.currentPatientList === null){
      this.currentPatientList = Object.keys(this.dicomdir_structure['PAT'])
      this.currentPatientList.sort()
    }
    let patient_id = this.currentPatientList[index]
    return this.dicomdir_structure['PAT'][patient_id]['data']['informationStr']
  }
  getNumberOfPatients(){
    if (this.currentPatientList === null){
      this.currentPatientList = Object.keys(this.dicomdir_structure['PAT'])
      this.currentPatientList.sort()
    }
    return this.currentPatientList.length
  }
  selectCurrentPatient(index){
    if (this.currentPatientList === null){
      this.currentPatientList = Object.keys(this.dicomdir_structure['PAT'])
      this.currentPatientList.sort()
    }
    this.currentPatientId = this.currentPatientList[index]
    this.currentStudyList = Object.keys(this.dicomdir_structure['PAT'][this.currentPatientId]['STU'])
    this.currentStudyList.sort()
  }


  // Study information
  getStudyInformationList(){
    let nStudies = this.getNumberOfStudies()
    let studyInformationList:any = []
    for(let index=0; index<nStudies; index++){
      studyInformationList.push(this.getStudyInformationFromIndex(index))
    }
    return studyInformationList
  }  
  getNumberOfStudies(){
    return this.currentStudyList.length
  }
  getStudyInformationFromIndex(index){
    let study_id = this.currentStudyList[index]
    return this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][study_id]['data']['informationStr']
  } 
  selectCurrentStudy(index){
    this.currentStudyId = this.currentStudyList[index]
    this.currentSerieList = Object.keys(this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'])
    this.currentSerieList.sort()
  }


  // Series information
  getSeriesInformationList(){
    let nSeries = this.getNumberOfSeries()
    let seriesInformationList:any = []
    for(let index=0; index<nSeries; index++){
      seriesInformationList.push(this.getSerieInformationFromIndex(index))
    }
    return seriesInformationList
  }
  getNumberOfSeries(){
    return this.currentSerieList.length
  }    
  getSerieInformationFromIndex(index){
    let serie_id = this.currentSerieList[index]
    return this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][serie_id]['data']['informationStr']
  } 
  selectCurrentSeries(index){
    this.currentSerieId = this.currentSerieList[index]
    // Evaluate if the serie has already been sorted
    if(!('sorted' in this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId])){
      // Sort the current serie
      this.sortSerieData(this.currentPatientId, this.currentStudyId, this.currentSerieId)
      this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['sorted'] = true
    }
  }
  getCurrentSeriesNumber(){
    try{
      return this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['data']['seriesNumber']
    }catch{
      return null
    }
  }


  // Process information
  getProcessesInformationList(){
    return this.userAuthenticationService.getUserLicenseDict()['licenses']
  }
  selectCurrentProcess(index){
    this.currentProcessIndex = index
  }
  getCurrentProcessKey(){
    return this.userAuthenticationService.getUserLicenseDict()['licenses'][this.currentProcessIndex].key
  }
  selectCurrentProcessFromKey(key){
    let processList = this.userAuthenticationService.getUserLicenseDict()['licenses']
    for(let index = 0; index < processList.length; index++ ){
      if(key === processList[index].key){
        this.selectCurrentProcess(index)
        return
      }
    }
  }







  // ---------- Methods that interact with backend cloud run functions ---------- //

  
  loadDicomAsync(file): Promise<cornerstone.Image | undefined>  {
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
            // Crea un Blob de ArrayBuffer
            const blob = new Blob([reader.result!], { type: 'application/dicom' });
            // Crea una URL de objeto
            const url = URL.createObjectURL(blob);
            // Usa Cornerstone para cargar la imagen
            cornerstone.loadImage('wadouri:' + url).then(function (image) {
                resolve(image);
            }).catch((err) => {
              console.error(err);
            });;
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
  }

  // Load serie data (Positioning image)
  async loadSeriesData(targetCallbackMethod: (data: any) => void){
    // Get the number of slice in the serie
    let nImagesInSerie = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'].length
    // Evaluate if the serie has already been loaded
    if('posImageInformation' in this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]){
      // Call the target method
      targetCallbackMethod(this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['posImageInformation'])
      // Finish the execution correctly
      return {'resultOk':true}
    }
    // Get image aspect ratio
    let pixelSpacing = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['pixelSpacing']
    let zPos_first = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['zPos_first']
    let zPos_last = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['zPos_last']
    // Get the slice spacing and the desired jump between slices for positioning image generation
    let minJump = 1
    let largestJumpForSerie = 1
    let sliceSpacing = 1
    if(nImagesInSerie > 1){
      sliceSpacing = Math.abs(zPos_last - zPos_first) / (nImagesInSerie - 1)
      minJump = Math.max(1, Math.round(this.minSliceSpacing / sliceSpacing))
      minJump = this.utilsService.highestPowerOfTwo(minJump)
      largestJumpForSerie = Math.min(this.largestJump, this.utilsService.highestPowerOfTwo(nImagesInSerie))
    }
    // Store the slice spacing value to be used in the subsequent http requests
    this.sliceSpacing = sliceSpacing
    // Generate bucket name and blob names
    let firebaseHash = this.firestoreService.getFirebaseHash()
    let bucketName = GlobalVariables.firestorage.rootDirectoryName_Dicom + '/' + this.currentFirebaseHash + '/' + firebaseHash
    let blobName = 'image_'
    // Get user Id Token
    let authorizationToken = await this.userAuthenticationService.getUserIdToken()
    let userEmail = this.userAuthenticationService.getUserEmail().toString()
    // Prepare the form data for the http request (values are "append", be care)
    //    Prepare the From data
    let formData = new FormData();
    formData.append('userEmail', userEmail)
    formData.append('nImagesInSerie', nImagesInSerie.toString())
    formData.append('bucketName', bucketName.toString())
    formData.append('blobName', blobName.toString())
    formData.append('pixelSpacing', pixelSpacing.toString())
    formData.append('zPos_first', zPos_first.toString())
    formData.append('zPos_last', zPos_last.toString())
    // Start uploading the data in background and generating the corresponding positioning images
    this.loadImagesAndGeneratePosImage(
      targetCallbackMethod, formData, userEmail, nImagesInSerie, largestJumpForSerie,
      minJump, firebaseHash, bucketName, blobName
    )
    // Return the result
    let resultDict:any = {'resultOk':true}
    return resultDict
  }

  async loadImagesAndGeneratePosImage(
    targetCallbackMethod: ((data: any) => void) | null,
    formData: FormData, userEmail: string, nImagesInSerie: number, largestJumpForSerie: number,
    minJump: number, firebaseHash: string, bucketName: string, blobName: string,
  ){
    // Get user Id Token
    let authorizationToken = await this.userAuthenticationService.getUserIdToken()
    // Upload the images according to the jump value
    for(let jump=largestJumpForSerie; jump>=minJump; jump /= 2){
      // Execute the pos image on change listener
      this.generatePosImage(
          targetCallbackMethod, formData, jump, firebaseHash, userEmail,
          this.currentPatientId, this.currentStudyId, this.currentSerieId)                                   
      //   Upload the images one by one
      for(let sliceIndex=0; sliceIndex<nImagesInSerie; sliceIndex+=jump){
        // Select the corresponding file, anonymize it and upload it asynchronously
        let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][sliceIndex]
        let objectPath = bucketName + '/' + blobName + sliceIndex.toString()
        this.anonymizeAndUploadDicomFile(file, objectPath, authorizationToken, sliceIndex)
      }
    }
  }

  async generatePosImage(
    targetCallbackMethod: ((data: any) => void) | null,
    formData: FormData, jump: number, firebaseHash: string, userEmail: string,
    patientId: string, studyId: string, serieId: string
  ){
    // Get user Id Token
    let authorizationToken = await this.userAuthenticationService.getUserIdToken()
    // Get the url string
    let urlString = environment.googleCloudRun.generic.posImage
    //    Launch the request and wait for the response
    formData.append('jump', jump.toString())  
    let resultDict = await this.httpService.executeHttpPostMethodSigned(urlString, formData, authorizationToken)
    // if the result is Ok, store it
    if(resultDict['resultOk']){
      // TODO: Changes to prevent freezing a lower quality image
      // Verify if the current image has a higher resolution than the existing one
      this.dicomdir_structure['PAT'][patientId]['STU'][studyId]['SER'][serieId]['firebaseHash'] = firebaseHash
      let currentPosImageInformation = this.dicomdir_structure['PAT'][patientId]['STU'][studyId]['SER'][serieId]['posImageInformation']
      if(!currentPosImageInformation){
        this.dicomdir_structure['PAT'][patientId]['STU'][studyId]['SER'][serieId]['posImageInformation'] = resultDict
      }else if(resultDict['jump'] < currentPosImageInformation['jump']){
        this.dicomdir_structure['PAT'][patientId]['STU'][studyId]['SER'][serieId]['posImageInformation'] = resultDict
      }
    }
    // If the patient, study and serie Ids are not the one being displayed in the UI, do not call the callback method
    if((patientId != this.currentPatientId) || (studyId != this.currentStudyId) || (serieId != this.currentSerieId)) return
    // Return the result by executing the callback method if it is not null
    if (targetCallbackMethod) return targetCallbackMethod(resultDict)
  }










  


  async anonymizeAndUploadDicomFile(file, objectPath, accessToken, sliceIndex){
    // Verify that the file has not been processed jet
    if(this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['uploaded'][sliceIndex] == true){
      return
    }
    // Store the information so it is not uploaded again
    this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['uploaded'][sliceIndex] = true
    // Anonymize dicom data and store it to another blob
    let arrayBuffer = await this.readFileArrayAsync(file)
    let dicomData = dcmjs.data.DicomMessage.readFile(arrayBuffer);
    let dataSet = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict);
    for(let key of Object.keys(dataSet)){
      if(key == 'StudyInstanceUID') dataSet[key] = this.getUniqueIdentifier('study', dataSet[key])
      else if(key == 'SeriesInstanceUID') dataSet[key] = this.getUniqueIdentifier('series', dataSet[key])
      else if(key == 'SOPInstanceUID') dataSet[key] = this.getUniqueIdentifier('image', dataSet[key])
      else if(!this.dicomKeysToKeep.includes(key)) delete dataSet[key]
    }
    // Store the new file
    dicomData.dict = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(dataSet);
    let newArrayBuffer = dicomData.write();
    // Create a Blob from the byte array
    let blob = new Blob([newArrayBuffer], {type: "application/dicom"});
    // Clear variables for collector
    arrayBuffer = null;
    dicomData = null;
    dataSet = null;
    newArrayBuffer = null;
    // Upload file
    let resultDict = await this.httpService.uploadFile(blob, objectPath, accessToken, this.userAuthenticationService.getUserEmail())
    // TODO: TBD - Manage error (!resultDict['resultOk'])
    if(!resultDict['resultOk']){
      // Store the information so it is uploaded again when needed
      this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['uploaded'][sliceIndex] = false
    }
  }

  readFileArrayAsync(file): Promise<any>  {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        try{
          let arrayBuffer = reader.result as ArrayBuffer;
          resolve(arrayBuffer);
        }catch{
          resolve(undefined)
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    })
  }





  
  /*
  // Classify dicom image
  async classifyDicom(currentSliceIndexes, targetCallbackMethod: (data: any) => void){
    // Get user Id Token
    let authorizationToken = await this.userAuthenticationService.getUserIdToken()
    let userId = this.userAuthenticationService.getUserId().toString()
    let minSliceIndex = Math.min(...currentSliceIndexes)
    let maxSliceIndex = Math.max(...currentSliceIndexes)
    // Generate bucket name and blob names
    let firebaseHash = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['firebaseHash']
    let bucketName = GlobalVariables.firestorage.rootDirectoryName_Dicom + '/' + this.currentFirebaseHash + '/' + firebaseHash
    let blobName = 'image_'  
    // Upload the files that have not been uploaded yet -- This is not necessary if all the images are uploaded when generating pos image
    //    Get the number of slice in the serie
    let nImagesInSerie = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'].length
    //    Get the offsets to the selected slices (if classification process uses surrounding images)
    let uploadImageIndex_min = Math.max(0, minSliceIndex-GlobalVariables.processes[this.getCurrentProcessKey()].minimumClassificationOffset)
    let uploadImageIndex_max = Math.min(nImagesInSerie, maxSliceIndex+GlobalVariables.processes[this.getCurrentProcessKey()].minimumClassificationOffset)
    //    Upload the images
    for(let sliceIndex=uploadImageIndex_min; sliceIndex<=uploadImageIndex_max; sliceIndex++){
      if(!this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['uploaded'][sliceIndex]){
        // Select the corresponding file, anonymize it and upload it asynchronously
        let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][sliceIndex]
        let objectPath = bucketName + '/' + blobName + sliceIndex.toString()
        this.anonymizeAndUploadDicomFile(file, objectPath, authorizationToken, sliceIndex)
      }
    }
    // Execute the http request
    let formData = new FormData();
    // Execute the http request
    formData.append('userId', userId)
    formData.append('minSliceIndex', minSliceIndex.toString())
    formData.append('maxSliceIndex', maxSliceIndex.toString())
    formData.append('nImagesInSerie', nImagesInSerie.toString())
    formData.append('classColors', JSON.stringify(GlobalVariables.processes[this.getCurrentProcessKey()].classColors))
    formData.append('bucketName', bucketName.toString())
    formData.append('blobName', blobName.toString())
    //    Get the url
    let urlString = environment.googleCloudRun[this.getCurrentProcessKey()].classify
    //    Launch the request
    let resultDict = await this.httpService.executeHttpPostMethodSigned(urlString, formData, authorizationToken)
    // Return the result by executing the callback method
    return targetCallbackMethod(resultDict)
  }
  */












  // Generate results
  async generateResults(currentSliceIndexes_vertebralLevels, currentSliceIndexes_artifacts, currentSliceIndexes_overflow,
    additionalInformationDict, nImagesInSerie, posImage_nRows, 
    posImage_nRowsOffset, patientId, studyId, targetCallbackMethod: (data: any) => void){
    // Get user Id Token
    let authorizationToken = await this.userAuthenticationService.getUserIdToken()
    let userEmail = this.userAuthenticationService.getUserEmail().toString()
    // Process key
    let processKey = this.getCurrentProcessKey()
    // Generate bucket name and blob names
    let firebaseHash = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['firebaseHash']
    let bucketName = GlobalVariables.firestorage.rootDirectoryName_Dicom + '/' + this.currentFirebaseHash + '/' + firebaseHash
    let blobName = 'image_'
    //    Upload all the images
    for(let sliceIndex=0; sliceIndex<nImagesInSerie; sliceIndex++){
      if(!this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['uploaded'][sliceIndex]){
        // Select the corresponding file, anonymize it and upload it asynchronously
        let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][sliceIndex]
        let objectPath = bucketName + '/' + blobName + sliceIndex.toString()
        this.anonymizeAndUploadDicomFile(file, objectPath, authorizationToken, sliceIndex)
      }
    }
    // Store the vertebral levels name list
    const vertebralLevelsNameList = GlobalVariables.additionalDataPopups.vertebralLevels.vertebralLevelsNameList;
    // Execute the http request
    let formData = new FormData();
    // Execute the http request    
    formData.append('userEmail', userEmail)
    formData.append('processKey', processKey.toString())
    formData.append('softwareVersion', GlobalVariables.softwareVersion)  
    formData.append('patientId', patientId) 
    formData.append('studyId', studyId)   
    try{
      formData.append('seriesNumber', this.getCurrentSeriesNumber().toString())
    }catch{
      formData.append('seriesNumber', '0')
    }
    formData.append('nImagesInSerie', nImagesInSerie.toString())
    formData.append('posImage_nRows', posImage_nRows.toString())
    formData.append('posImage_nRowsOffset', posImage_nRowsOffset.toString())
    formData.append('sliceIndexes_artifacts', JSON.stringify(currentSliceIndexes_artifacts))
    formData.append('sliceIndexes_overflow', JSON.stringify(currentSliceIndexes_overflow))
    formData.append('sliceIndexes_vertebralLevels', JSON.stringify(currentSliceIndexes_vertebralLevels))
    formData.append('additionalInformationDict', JSON.stringify(additionalInformationDict))
    formData.append('vertebralLevelsNameList', JSON.stringify(vertebralLevelsNameList))
    formData.append('bucketName', bucketName.toString())
    formData.append('blobName', blobName.toString())
    //    Get the url
    let urlString =  environment.googleCloudRun[this.getCurrentProcessKey()].results
    //    Launch the request
    let resultDict = await this.httpService.executeHttpPostMethodSigned(urlString, formData, authorizationToken)
    // Return the result
    return targetCallbackMethod(resultDict)
  }



























































  /*
  async displayImageLeft(canvas, currentSliceIndex, colorIverseFactor){
    // Select the corresponding file
    let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][currentSliceIndex]
    // Get the metadata
    let dataSet = await this.readFileAsync(file)
    let image_rescaleIntercept = dataSet!.floatString('x00281052')!
    let image_rescaleSlope = dataSet!.floatString('x00281053')!
    // Get the image
    let image = await this.loadDicomAsync(file)
    let pixelData = image.getPixelData()
    // Display the image
    let ctx = canvas.getContext('2d');
    // Create an image data "array"
    let imgData = ctx.createImageData(512, 512);
    // Visualization HU Range
    let minVal = this.visualization_minHU
    let maxVal = this.visualization_maxHU
    // Fill in the pixel values into the imageData
    let channels = 4
    for(let row=0; row<512; row++){
      for(let col=0; col<512; col++){
        let index = row*512+col
        let value = pixelData[index] * image_rescaleSlope + image_rescaleIntercept
        value = Math.round(255 * colorIverseFactor * Math.min(1, Math.max((value - minVal) / (maxVal - minVal), 0))) 
        imgData.data[index*channels] =  value
        imgData.data[index*channels+1] = value
        imgData.data[index*channels+2] = value 
        imgData.data[index*channels+3] = 255 
      }
    }
    // Set the canvas size
    canvas.width = 512;  
    canvas.height = 512;
    canvas.style.width = '512px';  
    canvas.style.height = '512px';
    // Display the image data into the canvas
    ctx.putImageData(imgData, 0, 0);
    // Return pixel data
  }
  */



  /*
  async displayImageLeft(canvas, currentSliceIndex){
    // Select the corresponding file
    let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][currentSliceIndex]      
    // Get the metadata
    let dataSet = await this.readFileAsync(file)
    let image_rescaleIntercept = dataSet!.floatString('x00281052')!
    let image_rescaleSlope = dataSet!.floatString('x00281053')!
    // Get the image
    let image = await this.loadDicomAsync(file)
    let pixelData = image.getPixelData()
    const nRows = image.rows;       
    const nCols = image.columns;
    // Display the image
    let offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = nCols;
    offscreenCanvas.height = nRows;
    let offscreenCtx = offscreenCanvas.getContext('2d');
    // Create an image data "array"
    let imgData = offscreenCtx.createImageData(nCols, nRows);
    // Visualization HU Range
    let minVal = this.visualization_minHU
    let maxVal = this.visualization_maxHU
    // Fill in the pixel values into the imageData
    let channels = 4
    for(let row=0; row<nRows; row++){
      for(let col=0; col<nCols; col++){
        let index = row*nCols+col
        let value = pixelData[index] * image_rescaleSlope + image_rescaleIntercept
        value = Math.round(255 * Math.min(1, Math.max((value - minVal) / (maxVal - minVal), 0))) 
        imgData.data[index*channels] =  value
        imgData.data[index*channels+1] = value
        imgData.data[index*channels+2] = value 
        imgData.data[index*channels+3] = 255 
      }
    }
    // Set the image in the offset canvas
    offscreenCtx.putImageData(imgData, 0, 0);
    // Set the canvas size
    canvas.width = 512;  
    canvas.height = 512;
    canvas.style.width = '512px';  
    canvas.style.height = '512px';
    // Display the image data into the canvas
    let ctx = canvas.getContext('2d');
    //ctx.putImageData(imgData, 0, 0, 512, 512);
    ctx.drawImage(offscreenCanvas, 0, 0, 512, 512);
    // Return pixel data
  }
  */




  async displayImageLeft(canvas, currentSliceIndex){
    // Select the corresponding file
    let file = this.dicomdir_structure['PAT'][this.currentPatientId]['STU'][this.currentStudyId]['SER'][this.currentSerieId]['IMG']['file'][currentSliceIndex]      
    // Get the metadata
    let dataSet = await this.readFileAsync(file)
    let image_rescaleIntercept = dataSet!.floatString('x00281052')!
    let image_rescaleSlope = dataSet!.floatString('x00281053')!
    // Get the image orientation
    const orientationStr = dataSet!.string('x00200037');
    if (orientationStr) this.currentImageOrientation = orientationStr.split('\\').map(parseFloat);
    else this.currentImageOrientation = [1, 0, 0, 0, 1, 0]
    // Get the image
    let image = await this.loadDicomAsync(file)
    let pixelData = image.getPixelData()
    const nRows = image.rows;       
    const nCols = image.columns;
    // Display the image
    let offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = nCols;
    offscreenCanvas.height = nRows;
    let offscreenCtx = offscreenCanvas.getContext('2d');
    // Create an image data "array"
    let imgData = offscreenCtx.createImageData(nCols, nRows);
    // Visualization HU Range
    let minVal = this.visualization_minHU
    let maxVal = this.visualization_maxHU
    // Fill in the pixel values into the imageData
    let channels = 4
    for(let row=0; row<nRows; row++){
      for(let col=0; col<nCols; col++){
        let index = row*nCols+col
        let value = pixelData[index] * image_rescaleSlope + image_rescaleIntercept
        value = Math.round(255 * Math.min(1, Math.max((value - minVal) / (maxVal - minVal), 0))) 
        imgData.data[index*channels] =  value
        imgData.data[index*channels+1] = value
        imgData.data[index*channels+2] = value 
        imgData.data[index*channels+3] = 255 
      }
    }
    // Set the image in the offset canvas
    offscreenCtx.putImageData(imgData, 0, 0);
    // Set the canvas size
    canvas.width = 512;  
    canvas.height = 512;
    canvas.style.width = '512px';  
    canvas.style.height = '512px';
    // Display the image data into the canvas
    let ctx = canvas.getContext('2d');
    // Modify image orientation if needed
    this.applyImageOrientationTransform(ctx, this.currentImageOrientation, canvas.width, canvas.height);
    // Drow the image
    ctx.drawImage(offscreenCanvas, 0, 0, 512, 512);
    // Return pixel data
  }


  // Function to flip or rotate the canvas based on the CT image orientation for visualization
  applyImageOrientationTransform(ctx, orientation, width, height) {
    ctx.save();
    const [rowX, rowY, rowZ, colX, colY, colZ] = orientation.map(parseFloat);
    if (rowX === 1 && colY === 1) {
      // No transformation needed
    } else if (rowX === -1 && colY === 1) {
      ctx.scale(-1, 1);
      ctx.translate(-width, 0);
    } else if (rowX === 1 && colY === -1) {
      ctx.scale(1, -1);
      ctx.translate(0, -height);
    } else if (rowX === -1 && colY === -1) {
      ctx.scale(-1, -1);
      ctx.translate(-width, -height);
    } else if (rowX === 0 && rowY === 1 && colX === -1 && colY === 0) {
      // Rotar 90°
      ctx.rotate(Math.PI / 2);
      ctx.translate(0, -height);
    } else if (rowX === 0 && rowY === -1 && colX === 1 && colY === 0) {
      // Rotar -90°
      ctx.rotate(-Math.PI / 2);
      ctx.translate(-width, 0);
    } else if (rowX === 0 && rowY === -1 && colX === -1 && colY === 0) {
      // Rotar 180°
      ctx.rotate(Math.PI);
      ctx.translate(-width, -height);
    } else {
      console.warn("Image Orientation (Patient) not supported:", orientation);
    }
  }



}
